import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { numberID, rupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Scissors, Layers, CheckCircle2, Coins, FileText, RotateCcw } from "lucide-react";
import { downloadPdf } from "./PurchaseDetail";
import { useAuth } from "@/context/AuthContext";

const kg = (g) => Number((g || 0) / 1000).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CatchWeightIssuePanel() {
  const { can, user } = useAuth();
  const canView = can("production.view");
  const isOwner = user?.role === "OWNER_CEO";
  const [enabled, setEnabled] = useState(false);
  const [rows, setRows] = useState(null);
  const [wh, setWh] = useState(null);
  const [create, setCreate] = useState(false);
  const [recon, setRecon] = useState(null);
  const [recoverFri, setRecoverFri] = useState(null);
  const [eligMap, setEligMap] = useState({});
  const [cost, setCost] = useState(null);
  const [rcScan, setRcScan] = useState(null);
  const [rcBusy, setRcBusy] = useState("");
  const [busy, setBusy] = useState(false);

  const loadScan = () => { if (!isOwner || !enabled) return; api.get("/production/catch-weight/roll-count/reconcile/scan").then((r) => setRcScan(r.data)).catch(() => setRcScan([])); };
  useEffect(loadScan, [enabled, isOwner]);
  const runReconcile = async (row) => {
    setRcBusy(row.item_id);
    try {
      const r = await api.post("/production/catch-weight/roll-count/reconcile", { item_id: row.item_id, warehouse_id: row.warehouse_id });
      toast.success(r.data.result === "ALREADY_SYNCED" ? `${row.item_name}: sudah sinkron` : `${row.item_name}: roll-count ${r.data.previous_on_hand} → ${r.data.resulting_on_hand}`);
      loadScan();
    } catch (e) { toast.error(apiError(e)); } finally { setRcBusy(""); }
  };

  // MR-2026-00007 ONE-RECORD RECONCILE — Owner-only, hard-locked to a single historical MR.
  const MR_FIX = "MR-2026-00007";
  const [mrFix, setMrFix] = useState(null); // eligibility response {eligible, already_reconciled, reason, mr_status}
  const [mrFixOpen, setMrFixOpen] = useState(false);
  const [mrFixBusy, setMrFixBusy] = useState(false);
  const loadMrFix = () => {
    if (!isOwner || !enabled) { setMrFix(null); return; }
    api.get(`/production/catch-weight/mr/${MR_FIX}/reconcile-issued/eligibility`)
      .then((r) => setMrFix(r.data)).catch(() => setMrFix(null));
  };
  useEffect(loadMrFix, [enabled, isOwner]);
  const runMrFix = async () => {
    setMrFixBusy(true);
    try {
      const r = await api.post(`/production/catch-weight/mr/${MR_FIX}/reconcile-issued`);
      setMrFixOpen(false);
      if (r.data.result === "ALREADY_RECONCILED") {
        toast.success(`${MR_FIX}: sudah Dikeluarkan (tidak ada perubahan)`);
        loadMrFix();
      } else {
        toast.success(`${MR_FIX}: status diperbaiki → Dikeluarkan`);
        loadMrFix();
        setTimeout(() => window.location.reload(), 1000); // refresh daftar MR parent agar tampil Dikeluarkan
      }
    } catch (e) { toast.error(apiError(e)); } finally { setMrFixBusy(false); }
  };

  useEffect(() => { api.get("/purchasing/catch-weight/config").then((r) => setEnabled(!!r.data.fabric_catch_weight_enabled)).catch(() => setEnabled(false)); }, []);
  useEffect(() => { api.get("/inventory/warehouses").then((r) => setWh(r.data.find((w) => w.type === "KONVEKSI"))); }, []);
  const load = () => { if (!enabled) return; setRows(null); api.get("/production/catch-weight/roll-issues").then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(load, [enabled]);

  // CW-FRI-RECOVERY-UI-2: eligibility-based — button only shows when backend eligible=true (Owner only)
  useEffect(() => {
    if (!isOwner || !rows) { setEligMap({}); return; }
    const stuck = rows.filter((r) => r.status === "PROCESSING");
    if (stuck.length === 0) { setEligMap({}); return; }
    let cancelled = false;
    Promise.all(stuck.map((r) => api.get(`/production/catch-weight/fri/${r.fri_no}/recover-stuck/eligibility`)
      .then((res) => [r.fri_no, res.data]).catch(() => [r.fri_no, { eligible: false }])))
      .then((pairs) => { if (!cancelled) setEligMap(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [rows, isOwner]);

  const confirm = async (iss) => {
    setBusy(true);
    try { await api.post(`/production/catch-weight/roll-issues/${iss.id}/confirm`); toast.success(`${iss.fri_no} dikonfirmasi — roll keluar ke Cutting`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async (iss) => {
    setBusy(true);
    try { await api.post(`/production/catch-weight/roll-issues/${iss.id}/cancel`); toast.success(`${iss.fri_no} dibatalkan`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!enabled) return null;
  return (
    <div className="mt-8" data-testid="cw-issue-panel">
      {isOwner && mrFix && (mrFix.eligible || mrFix.already_reconciled) && (
        <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid="cw-mr-fix-panel">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-indigo-800"><RotateCcw className="h-4 w-4" /> Perbaikan Status Material Request Historis</div>
          {mrFix.eligible ? (
            <>
              <p className="mb-2 text-xs text-indigo-700" data-testid="cw-mr-fix-warning">FRI terkait sudah selesai direkonsiliasi, tetapi status Material Request historis <b>{MR_FIX}</b> masih <b>Draft</b>. Tindakan ini hanya mengubah status MR menjadi <b>Dikeluarkan</b> dan tidak mengubah stok, roll, KG, FRI, atau PO.</p>
              <Button size="sm" disabled={mrFixBusy} onClick={() => setMrFixOpen(true)} data-testid="cw-mr-fix-btn" className="bg-indigo-600 hover:bg-indigo-500">Perbaiki Status {MR_FIX}</Button>
            </>
          ) : (
            <p className="text-xs font-semibold text-emerald-700" data-testid="cw-mr-fix-done">{MR_FIX} sudah diperbaiki — status Dikeluarkan.</p>
          )}
        </div>
      )}
      {isOwner && rcScan && rcScan.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="cw-roll-count-reconcile-panel">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800"><RotateCcw className="h-4 w-4" /> Rekonsiliasi Roll-Count (Catch Weight)</div>
          <p className="mb-2 text-xs text-amber-700">Saldo roll-count berbeda dari custody <b>fabric_rolls</b> (AVAILABLE/PARTIAL). Sinkronkan tanpa mengubah KG, FRI/MR/PO, atau applied_ops.</p>
          <div className="space-y-1.5">
            {rcScan.map((row) => (
              <div key={`${row.item_id}-${row.warehouse_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm" data-testid={`cw-rc-row-${row.item_id}`}>
                <span className="text-slate-700">{row.item_name}</span>
                <span className="text-xs text-slate-500">on_hand <b>{numberID(row.current_on_hand)}</b> → expected <b>{numberID(row.expected_count)}</b> <span className={row.delta > 0 ? "text-emerald-600" : "text-rose-600"}>({row.delta > 0 ? "+" : ""}{numberID(row.delta)})</span></span>
                <Button size="sm" disabled={rcBusy === row.item_id} onClick={() => runReconcile(row)} data-testid={`cw-rc-apply-${row.item_id}`} className="bg-amber-600 hover:bg-amber-500">{rcBusy === row.item_id ? "..." : "Sinkronkan"}</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-700"><Layers className="h-4 w-4 text-indigo-600" /> Material Issue Kain (Catch Weight) <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">per-Roll</span></h3>
        {wh && <Button size="sm" onClick={() => setCreate(true)} data-testid="cw-new-issue-btn" className="bg-indigo-600 hover:bg-indigo-500"><Scissors className="mr-1.5 h-4 w-4" /> Issue Roll ke Cutting</Button>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada issue kain catch-weight" subtitle="Keluarkan roll kain ke Cutting via tombol di atas." icon={Layers} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No FRI</TableHead><TableHead>Kain</TableHead><TableHead>PO Produksi</TableHead><TableHead className="text-right">Roll</TableHead><TableHead className="text-right">Issued KG</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((iss) => {
                const issuedG = iss.lines.reduce((s, l) => s + (l.issued_weight_g || 0), 0);
                return (
                  <TableRow key={iss.id} data-testid={`fri-row-${iss.fri_no}`}>
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">{iss.fri_no}</TableCell>
                    <TableCell className="text-sm">{iss.item_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{iss.po_prod_no || "-"}</TableCell>
                    <TableCell className="text-right">{numberID(iss.lines.length)}</TableCell>
                    <TableCell className="text-right font-semibold">{kg(issuedG)} KG</TableCell>
                    <TableCell><StatusBadge status={iss.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {iss.status === "DRAFT" && <Button size="sm" disabled={busy} onClick={() => confirm(iss)} data-testid={`fri-confirm-${iss.fri_no}`} className="bg-emerald-600 hover:bg-emerald-500">Konfirmasi</Button>}
                        {iss.status === "DRAFT" && <Button size="sm" variant="ghost" disabled={busy} onClick={() => cancel(iss)} className="text-rose-600">Batal</Button>}
                        {(iss.status === "CONFIRMED" || iss.status === "PROCESSING") && <Button size="sm" disabled={busy} onClick={() => setRecon(iss)} data-testid={`fri-reconcile-${iss.fri_no}`} className="bg-indigo-600 hover:bg-indigo-500"><Scissors className="mr-1 h-3.5 w-3.5" /> Rekonsiliasi</Button>}
                        {iss.status === "RECONCILED" && <Button size="sm" variant="outline" onClick={() => setCost(iss)} data-testid={`fri-cost-${iss.fri_no}`}><Coins className="mr-1 h-3.5 w-3.5 text-emerald-600" /> Biaya</Button>}
                        {isOwner && iss.status === "PROCESSING" && eligMap[iss.fri_no]?.eligible && <Button size="sm" variant="outline" disabled={busy} onClick={() => setRecoverFri({ ...iss, _mode: "reverse" })} data-testid={`fri-recover-${iss.fri_no}`} className="border-amber-300 text-amber-700 hover:bg-amber-50"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Pulihkan Issue Macet</Button>}
                        {isOwner && iss.status === "PROCESSING" && !eligMap[iss.fri_no]?.eligible && eligMap[iss.fri_no]?.partial_wedge?.eligible && <Button size="sm" variant="outline" disabled={busy} onClick={() => setRecoverFri({ ...iss, _mode: "partial" })} data-testid={`fri-reset-partial-${iss.fri_no}`} className="border-orange-300 text-orange-700 hover:bg-orange-50"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset Issue Macet (Partial)</Button>}
                        {canView && <Button size="sm" variant="outline" onClick={() => downloadPdf(`/pdf/fabric-roll-issue/${iss.id}`)} data-testid={`fri-pdf-${iss.fri_no}`}><FileText className="mr-1 h-3.5 w-3.5 text-indigo-600" /> PDF</Button>}
                        {canView && iss.status === "RECONCILED" && <Button size="sm" variant="outline" onClick={() => downloadPdf(`/pdf/catch-weight-reconciliation/${iss.id}`)} data-testid={`fri-recon-pdf-${iss.fri_no}`}><FileText className="mr-1 h-3.5 w-3.5 text-emerald-600" /> PDF Rekonsiliasi</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {create && wh && <CreateIssueDialog wh={wh} onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}
      {recon && <ReconcileDialog issue={recon} onClose={() => setRecon(null)} onSaved={() => { setRecon(null); load(); }} />}
      {cost && <CostSummaryDialog issue={cost} onClose={() => setCost(null)} />}
      {recoverFri && <RecoverStuckDialog issue={recoverFri} onClose={() => setRecoverFri(null)} onDone={() => { setRecoverFri(null); load(); }} />}
      <Dialog open={mrFixOpen} onOpenChange={(o) => { if (!o) setMrFixOpen(false); }}>
        <DialogContent data-testid="cw-mr-fix-dialog">
          <DialogHeader><DialogTitle>Perbaiki Status {MR_FIX}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <p>FRI terkait sudah selesai direkonsiliasi, tetapi status Material Request historis <b>{MR_FIX}</b> masih <b>Draft</b>.</p>
            <p className="rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-700">Tindakan ini <b>hanya</b> mengubah status MR menjadi <b>Dikeluarkan</b>. Tidak mengubah stok, roll, KG, FRI, atau PO. Terkunci hanya untuk {MR_FIX}.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMrFixOpen(false)} data-testid="cw-mr-fix-cancel">Batal</Button>
            <Button onClick={runMrFix} disabled={mrFixBusy} data-testid="cw-mr-fix-confirm" className="bg-indigo-600 hover:bg-indigo-500">{mrFixBusy ? "..." : "Ya, Perbaiki Status"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecoverStuckDialog({ issue, onClose, onDone }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const partial = issue._mode === "partial";
  const required = `${partial ? "RESET" : "RECOVER"} ${issue.fri_no}`;
  const run = async () => {
    setBusy(true);
    try {
      const url = partial
        ? `/production/catch-weight/fri/${issue.fri_no}/recover-partial-wedge`
        : `/production/catch-weight/fri/${issue.fri_no}/recover-stuck`;
      const r = await api.post(url);
      toast.success(r.data.result === "ALREADY_RECOVERED" ? "Sudah dipulihkan sebelumnya" : `${issue.fri_no} dikembalikan ke Draft`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="fri-recover-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><RotateCcw className="h-5 w-5 text-amber-600" /> {partial ? "Reset Partial" : "Pulihkan"} {issue.fri_no}?</DialogTitle></DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
          <li>FRI kembali ke <b>Draft</b></li>
          <li>Roll yang dimiliki FRI ini kembali ke status semula (<b>Available</b>)</li>
          {partial && <li>Line PENDING/tak-dimiliki: roll tidak diubah</li>}
          <li>Berat roll (KG) <b>tidak berubah</b></li>
          <li>Tidak membuat movement stock / tidak mengubah saldo</li>
          <li>PO/MR tidak berubah</li>
        </ul>
        <div className="mt-2">
          <Label className="text-xs">Ketik untuk konfirmasi: <span className="font-mono font-semibold">{required}</span></Label>
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="fri-recover-confirm-input" className="mt-1 font-mono" placeholder={required} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Batal</Button>
          <Button onClick={run} disabled={busy || txt !== required} data-testid="fri-recover-confirm-btn" className="bg-amber-600 hover:bg-amber-500">Pulihkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CostSummaryDialog({ issue, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/costing/catch-weight/actual-costing", { params: { production_order_id: issue.production_order_id || undefined } })
      .then((r) => { const m = (r.data || []).find((x) => x.fri_no === issue.fri_no); setD(m || false); })
      .catch(() => setD(false));
  }, [issue]);
  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2"><span className="text-xs text-slate-500">{label}</span><span className="text-sm font-semibold text-slate-800">{value}</span></div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cw-cost-summary-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Coins className="h-5 w-5 text-emerald-600" /> Ringkasan Biaya — {issue.fri_no}</DialogTitle></DialogHeader>
        {d === null ? <Loading /> : !d ? <p className="text-sm text-slate-500">Data biaya belum tersedia.</p> : (
          <div>
            <Row label="Actual Used KG" value={`${kg(d.used_weight_g)} KG`} />
            <Row label="Waste KG" value={`${kg(d.waste_weight_g)} KG`} />
            <Row label="Returned KG" value={`${kg(d.returned_weight_g)} KG`} />
            <Row label="Good Output" value={d.good_output_available ? `${numberID(d.good_output_qty)} pcs` : "Belum tersedia"} />
            <Row label="Actual g/pcs" value={d.actual_grams_per_piece != null ? d.actual_grams_per_piece : "Belum tersedia"} />
            <Row label="Fabric Cost (Used)" value={rupiah(d.actual_used_fabric_cost)} />
            <Row label="Waste Cost (terpisah)" value={rupiah(d.actual_waste_cost)} />
            <p className="mt-3 text-[11px] text-slate-400">Read-only dari snapshot cost roll. Analitik lengkap di Costing → Aktual vs Standar (Kain).</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateIssueDialog({ wh, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState([]);
  const [mrs, setMrs] = useState([]);
  const [itemId, setItemId] = useState("");
  const [poId, setPoId] = useState("");
  const [mrId, setMrId] = useState("");
  const [plan, setPlan] = useState(null);
  const [rolls, setRolls] = useState(null);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/inventory/items", { params: { warehouse_id: wh.id, status: "active" } }).then((r) => setItems(r.data.filter((it) => it.tracking_mode === "CATCH_WEIGHT_ROLL")));
    api.get("/production/production-orders").then((r) => setPos(r.data.filter((p) => ["Disetujui", "Proses Produksi"].includes(p.status)))).catch(() => {});
    api.get("/production/material-requests").then((r) => setMrs(r.data)).catch(() => {});
  }, [wh.id]);
  useEffect(() => { setMrId(""); }, [poId, itemId]);
  const candMrs = mrs.filter((m) => m.status === "Draft" && m.production_order_id === poId && (m.items || []).some((it) => it.item_id === itemId));
  useEffect(() => {
    setPlan(null);
    if (!poId) return;
    const params = mrId ? { material_request_id: mrId } : {};
    api.get(`/production/production-orders/${poId}/fabric-plan`, { params }).then((r) => setPlan(r.data)).catch(() => setPlan(null));
  }, [poId, mrId]);
  useEffect(() => {
    if (!itemId) { setRolls(null); return; }
    setRolls(null); setSel({});
    api.get("/production/catch-weight/suggest-rolls", { params: { inventory_item_id: itemId, warehouse_id: wh.id } })
      .then((r) => { setRolls(r.data.rolls); })
      .catch((e) => { toast.error(apiError(e)); setRolls([]); });
  }, [itemId, wh.id]);
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const totalG = (rolls || []).filter((r) => sel[r.roll_id]).reduce((s, r) => s + r.remaining_weight_g, 0);
  const poRemaining = plan && plan.remaining_roll_qty != null ? plan.remaining_roll_qty : null;
  const mrItem = plan?.mr?.items?.find((it) => it.item_id === itemId);
  const mrRemaining = plan?.mr && mrItem ? (Number(mrItem.qty) - (plan.mr.effective_roll_usage || 0)) : null;
  const remaining = (poRemaining == null && mrRemaining == null) ? null
    : Math.max(0, Math.min(poRemaining == null ? Infinity : poRemaining, mrRemaining == null ? Infinity : mrRemaining));
  const limitReached = remaining != null && chosen.length >= remaining;
  const planMissing = !!plan && plan.planned_fabric_roll_qty == null;
  const save = async () => {
    if (!poId) return toast.error("Pilih Production Order");
    if (!itemId) return toast.error("Pilih kain catch-weight");
    if (!mrId) return toast.error("Pilih Material Request");
    if (planMissing) return toast.error("Tetapkan Kebutuhan Kain pada PO terlebih dahulu");
    if (chosen.length === 0) return toast.error("Pilih minimal 1 roll");
    if (remaining != null && chosen.length > remaining) return toast.error(`Maksimal ${remaining} roll sesuai sisa kebutuhan`);
    setSaving(true);
    try {
      await api.post("/production/catch-weight/roll-issues", { warehouse_id: wh.id, inventory_item_id: itemId, roll_ids: chosen, production_order_id: poId, material_request_id: mrId });
      toast.success("Fabric Roll Issue dibuat (DRAFT) — konfirmasi untuk keluarkan ke Cutting"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cw-create-issue-dialog">
        <DialogHeader><DialogTitle className="font-display">Issue Roll Kain ke Cutting</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Production Order</Label>
            <Select value={poId} onValueChange={setPoId}><SelectTrigger className="mt-1" data-testid="cw-issue-po"><SelectValue placeholder="Pilih PO..." /></SelectTrigger>
              <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_prod_no} · {p.fabric_name} {p.color_name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Kain Catch Weight</Label>
            <Select value={itemId} onValueChange={setItemId}><SelectTrigger className="mt-1" data-testid="cw-issue-item"><SelectValue placeholder="Pilih kain..." /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Material Request</Label>
            <Select value={mrId} onValueChange={setMrId} disabled={!poId || !itemId}><SelectTrigger className="mt-1" data-testid="cw-issue-mr"><SelectValue placeholder="Pilih MR..." /></SelectTrigger>
              <SelectContent>{candMrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.mr_no}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        {poId && itemId && candMrs.length === 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="cw-issue-no-mr">Belum ada Material Request Draft untuk PO &amp; kain ini. Buat Material Request kain terlebih dahulu sebelum Issue Roll ke Cutting.</p>}
        {plan && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm" data-testid="cw-issue-plan">
            <span className="font-semibold text-slate-700">Kebutuhan Kain PO: </span>
            {plan.planned_fabric_roll_qty == null
              ? <span className="text-rose-600">belum ditetapkan — tetapkan Kebutuhan Kain pada PO dahulu</span>
              : <span>Rencana <b>{numberID(plan.planned_fabric_roll_qty)}</b> · Dialokasikan <b>{numberID(plan.effective_roll_usage)}</b> · Sisa PO <b>{numberID(plan.remaining_roll_qty)}</b>{mrRemaining != null && <> · Sisa MR <b>{numberID(mrRemaining)}</b></>} Roll</span>}
          </div>
        )}
        {planMissing && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="cw-issue-plan-missing">Tetapkan Kebutuhan Kain pada PO terlebih dahulu.</p>}
        {remaining === 0 && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="cw-issue-fulfilled">Kebutuhan kain PO sudah terpenuhi. Tidak ada roll yang dapat di-issue.</p>}
        {itemId && (!rolls ? <Loading /> : rolls.length === 0 ? <EmptyState title="Tidak ada roll usable" subtitle="Hanya roll AVAILABLE/PARTIAL yang dapat di-issue (QUARANTINE dikecualikan)." icon={Layers} /> : (
          <div className="rounded-xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Roll No</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Sisa KG</TableHead><TableHead>Tgl Terima</TableHead><TableHead className="text-right">Cost/KG</TableHead></TableRow></TableHeader>
              <TableBody>
                {rolls.map((r) => {
                  const checked = !!sel[r.roll_id];
                  const disabled = (!checked && limitReached) || planMissing;
                  return (
                    <TableRow key={r.roll_id} data-testid={`cw-roll-opt-${r.roll_no}`} className={disabled ? "opacity-40" : ""}>
                      <TableCell><input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => setSel({ ...sel, [r.roll_id]: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-indigo-600" data-testid={`cw-roll-check-${r.roll_no}`} /></TableCell>
                      <TableCell className="font-mono text-xs font-semibold text-indigo-700">{r.roll_no}</TableCell>
                      <TableCell><StatusBadge status={r.status === "PARTIAL" ? "LOW" : "OK"} label={r.status === "PARTIAL" ? "Sisa Roll" : "Tersedia"} /></TableCell>
                      <TableCell className="text-right font-semibold">{kg(r.remaining_weight_g)} KG</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(r.received_at)}</TableCell>
                      <TableCell className="text-right text-xs">{rupiah(r.unit_cost_per_kg)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ))}
        {chosen.length > 0 && <p className="text-sm text-slate-600" data-testid="cw-issue-total">{chosen.length} roll dipilih{remaining != null ? ` (maks ${remaining})` : ""} · estimasi {kg(totalG)} KG akan di-issue ke Cutting</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving || !poId || !itemId || !mrId || planMissing || chosen.length === 0} data-testid="cw-issue-save-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Buat Issue (DRAFT)"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconcileDialog({ issue, onClose, onSaved }) {
  const [lines, setLines] = useState(issue.lines.map((l) => ({ id: l.id, roll_no: l.roll_no, issued_g: l.issued_weight_g, used: "", waste: "", ret: "" })));
  const [good, setGood] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (i, k, v) => setLines(lines.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const pendingG = (l) => l.issued_g - Math.round((Number(l.used || 0) + Number(l.waste || 0) + Number(l.ret || 0)) * 1000);
  const save = async () => {
    for (const l of lines) {
      if (pendingG(l) !== 0) return toast.error(`Roll ${l.roll_no}: Used+Waste+Return harus = Issued (pending ${kg(pendingG(l))} KG)`);
    }
    setSaving(true);
    try {
      await api.post(`/production/catch-weight/roll-issues/${issue.id}/reconcile`, {
        good_output_qty: good === "" ? null : Number(good), notes,
        lines: lines.map((l) => ({ issue_line_id: l.id, used_weight_kg: Number(l.used || 0), waste_weight_kg: Number(l.waste || 0), returned_weight_kg: Number(l.ret || 0) })),
      });
      toast.success("Rekonsiliasi Cutting selesai"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cw-reconcile-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Scissors className="h-5 w-5 text-indigo-600" /> Rekonsiliasi Cutting — {issue.fri_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-xs" data-testid="recon-context">
          <div><span className="text-slate-400">No FRI:</span> <span className="font-mono font-semibold text-indigo-700">{issue.fri_no}</span></div>
          <div><span className="text-slate-400">PO Produksi:</span> <span className="font-semibold text-slate-700">{issue.po_prod_no || "-"}</span></div>
          <div><span className="text-slate-400">Material Request:</span> <span className="font-semibold text-slate-700">{issue.mr_no || "-"}</span></div>
          <div><span className="text-slate-400">Total Issued:</span> <span className="font-semibold text-slate-700">{kg(issue.lines.reduce((s, l) => s + (l.issued_weight_g || 0), 0))} KG</span></div>
        </div>
        <p className="text-xs text-slate-500">Isi pemakaian (Used), susut (Waste), dan sisa dikembalikan (Return) dalam KG. Invariant: Used + Waste + Return = Issued.</p>
        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Roll ID</TableHead><TableHead className="text-right">Issued</TableHead><TableHead>Used (KG)</TableHead><TableHead>Waste (KG)</TableHead><TableHead>Return (KG)</TableHead><TableHead className="text-right">Pending</TableHead></TableRow></TableHeader>
            <TableBody>
              {lines.map((l, i) => {
                const pend = pendingG(l);
                return (
                  <TableRow key={l.id} data-testid={`recon-line-${l.roll_no}`}>
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">{l.roll_no}</TableCell>
                    <TableCell className="text-right font-semibold">{kg(l.issued_g)} KG</TableCell>
                    <TableCell><Input type="number" min="0" step="0.001" className="h-8 w-24" value={l.used} onChange={(e) => set(i, "used", e.target.value)} data-testid={`recon-used-${l.roll_no}`} /></TableCell>
                    <TableCell><Input type="number" min="0" step="0.001" className="h-8 w-24" value={l.waste} onChange={(e) => set(i, "waste", e.target.value)} data-testid={`recon-waste-${l.roll_no}`} /></TableCell>
                    <TableCell><Input type="number" min="0" step="0.001" className="h-8 w-24" value={l.ret} onChange={(e) => set(i, "ret", e.target.value)} data-testid={`recon-ret-${l.roll_no}`} /></TableCell>
                    <TableCell className={`text-right font-semibold ${pend === 0 ? "text-emerald-600" : "text-rose-600"}`}>{kg(pend)} KG</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Good Output (pcs, opsional)</Label><Input type="number" min="0" className="mt-1" value={good} onChange={(e) => setGood(e.target.value)} data-testid="recon-good-output" /></div>
          <div><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="recon-save-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Finalisasi Rekonsiliasi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
